import { Node, mergeAttributes } from "@tiptap/core"

// Strict YouTube video id: 11 chars, base64url-ish alphabet. Used both
// client-side (here) and server-side (Pages::MarkdownRenderer). Keep
// the two regexes in sync if you ever loosen this.
export const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/

// Pure function: returns the 11-char video id or null. Accepts:
//   - a bare id
//   - youtube.com/watch?v=ID (with or without &extras)
//   - youtu.be/ID
//   - youtube.com/shorts/ID
//   - youtube.com/embed/ID
//   - the m.youtube.com mobile equivalents
// Anything else returns null. Caller decides how to surface the error.
export function parseYouTubeUrl(input) {
  if (typeof input !== "string") return null
  const trimmed = input.trim()
  if (!trimmed) return null
  if (YOUTUBE_ID_RE.test(trimmed)) return trimmed

  let url
  try { url = new URL(trimmed) } catch { return null }

  const host = url.hostname.toLowerCase().replace(/^www\.|^m\./, "")
  let id = null

  if (host === "youtu.be") {
    id = url.pathname.slice(1).split("/")[0]
  } else if (host === "youtube.com" || host === "youtube-nocookie.com") {
    if (url.pathname === "/watch") {
      id = url.searchParams.get("v")
    } else {
      const m = url.pathname.match(/^\/(shorts|embed|v)\/([^/?#]+)/)
      if (m) id = m[2]
    }
  }

  return id && YOUTUBE_ID_RE.test(id) ? id : null
}

// In-editor preview: 16:9 thumbnail card from i.ytimg.com with a play
// button overlay. The whole node is a non-editable atom — clicking it
// inside the editor does nothing (no playback in the editor).
function renderPreviewHTML(videoId) {
  return [
    "div",
    {
      "data-youtube-embed": "",
      "data-video-id": videoId,
      // Inline aspect-ratio: the @tailwindcss/aspect-ratio plugin in
      // tailwind.config.js disables Tailwind core's `aspect-video`
      // utility, so we set the ratio inline (same as the public partial).
      style: "aspect-ratio: 16 / 9",
      class: "relative w-full max-w-2xl my-4 rounded overflow-hidden border border-gray-200 dark:border-gray-700 bg-black select-none"
    },
    [
      "img",
      {
        src: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
        alt: "YouTube video",
        loading: "lazy",
        class: "w-full h-full object-cover opacity-90"
      }
    ],
    [
      "div",
      { class: "absolute inset-0 flex items-center justify-center pointer-events-none" },
      [
        "div",
        {
          class: "rounded-full bg-black/70 text-white px-5 py-3 text-2xl",
          "aria-hidden": "true"
        },
        "▶"
      ]
    ],
    [
      "div",
      { class: "absolute bottom-2 left-2 text-xs font-semibold text-white bg-red-600 rounded px-2 py-0.5" },
      "YouTube"
    ]
  ]
}

export const YouTubeEmbed = Node.create({
  name: "youtubeEmbed",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      videoId: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-video-id"),
        renderHTML: (attrs) => attrs.videoId ? { "data-video-id": attrs.videoId } : {}
      }
    }
  },

  parseHTML() {
    return [{ tag: "div[data-youtube-embed]" }]
  },

  renderHTML({ node, HTMLAttributes }) {
    const videoId = node.attrs.videoId
    // mergeAttributes lets external HTMLAttributes (e.g. from
    // node-decoration) flow through, but we want our preview structure;
    // ignore HTMLAttributes for the inner content and only honour them
    // on the outer wrapper attributes via the array's [1] slot.
    const tree = renderPreviewHTML(videoId)
    tree[1] = mergeAttributes(tree[1], HTMLAttributes)
    return tree
  },

  addCommands() {
    return {
      setYouTubeEmbed: (attrs) => ({ commands }) =>
        commands.insertContent({ type: this.name, attrs })
    }
  },

  // tiptap-markdown integration. Uses parse.updateDOM (post-render
  // markdown-it pass) — simpler than registering a markdown-it plugin
  // and good enough for a paragraph-shaped directive. On serialise we
  // emit the directive on its own block.
  addStorage() {
    return {
      markdown: {
        serialize(state, node) {
          state.write(`{{youtube:${node.attrs.videoId}}}`)
          state.closeBlock(node)
        },
        parse: {
          updateDOM(rootEl) {
            const directiveRe = /^\{\{youtube:([A-Za-z0-9_-]{11})\}\}$/
            rootEl.querySelectorAll("p").forEach((p) => {
              const m = p.textContent.trim().match(directiveRe)
              if (!m) return
              const div = document.createElement("div")
              div.setAttribute("data-youtube-embed", "")
              div.setAttribute("data-video-id", m[1])
              p.replaceWith(div)
            })
          }
        }
      }
    }
  }
})
